import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { HeaderField } from '~/components/common/HeaderField';
import {
  contractMonthlyPrice,
  formatCurrency,
  Row,
  yesNo,
} from '~/components/contractReview/contractDetailShared';
import type { ContractReview } from '~/components/contractReview/types';
import { pageBandDividerColor } from '~/theme';

/** Coverage, Pricing and Additional details — the row is sized for all three whether
 * or not the last one has anything to show. */
const COLUMN_COUNT = 3;

/**
 * The blocks of the header's second row: what the service covers, what it costs, and
 * whatever else is on the contract. Three columns at `lg`, two at `md`, one below — a
 * fixed three-up would leave each block narrower than the drawer they came out of.
 */
export function ContractSummaryFields({
  contract,
  stacked = false,
}: {
  contract: ContractReview | null;
  stacked?: boolean;
}) {
  const { currencyCode } = useContext(UserContext);
  const monthlyTotal = contract ? contractMonthlyPrice(contract) : 0;
  const hasExtras = Boolean(contract && (contract.customDetails.length > 0 || contract.notes));

  const skeleton = (
    <Stack gap={0.25}>
      <Skeleton width="90%" />
      <Skeleton width="90%" />
      <Skeleton width="70%" />
    </Stack>
  );

  const columns: ReactNode[] = [
    <HeaderField key="coverage" label="Coverage">
      {contract ? (
        <Box>
          <Row label="Contract paper" value={contract.contractPaper || '—'} />
          <Row label="Business hours" value={contract.businessHours || '—'} />
          <Row label="Phone monitoring" value={yesNo(contract.phoneMonitoring)} />
          <Row label="Fire service" value={yesNo(contract.fireService)} />
          <Row label="Pressure test" value={yesNo(contract.pressureTest)} />
        </Box>
      ) : (
        skeleton
      )}
    </HeaderField>,

    <HeaderField key="pricing" label="Pricing">
      {contract ? (
        <Box>
          <Row label="Tax-inclusive" value={yesNo(contract.monthlyPriceTaxInclusive)} />
          <Row
            label="Escalation cap"
            value={
              contract.escalationRate > 0 ? `${(contract.escalationRate * 100).toFixed(1)}%` : '—'
            }
          />
          <Row
            label="Adjustment rate (yearly)"
            value={
              contract.hourlyFeeAdjustmentRate > 0 ? `${contract.hourlyFeeAdjustmentRate}%` : '—'
            }
          />
          <Row
            label="Parts & materials markup"
            value={
              contract.partsAndMaterialsMarkup > 0 ? `${contract.partsAndMaterialsMarkup}%` : '—'
            }
          />

          <Box
            display="flex"
            gap={2}
            justifyContent="space-between"
            alignItems="baseline"
            mt={0.75}
            pt={0.75}
            borderTop="2px solid"
            borderColor="divider"
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Monthly (total)
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {formatCurrency({ value: monthlyTotal, currencyCode })}
            </Typography>
          </Box>
        </Box>
      ) : (
        skeleton
      )}
    </HeaderField>,
  ];

  // Only when the contract has any: a labelled but empty third column would look as
  // though something failed to load. Its track in the grid is reserved either way, so
  // the two blocks beside it don't move.
  if (hasExtras && contract) {
    columns.push(
      <HeaderField key="extras" label="Additional details">
        <Box>
          {contract.customDetails.map((detail) => (
            <Row key={detail.label} label={detail.label} value={detail.value} />
          ))}
          {contract.notes ? (
            <Box mt={contract.customDetails.length > 0 ? 1.5 : 0}>
              <Typography variant="caption" color="text.secondary" display="block">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {contract.notes}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </HeaderField>,
    );
  }

  return (
    <Box
      display="grid"
      // Always the three columns and the divider track between each pair, so the blocks
      // don't change width as a consultant moves between contracts. Below `md` the grid is a
      // single column and the `auto` tracks collapse to nothing.
      gridTemplateColumns={
        stacked
          ? '1fr'
          : {
              xs: '1fr',
              md: Array.from({ length: COLUMN_COUNT }, () => 'minmax(0, 1fr)').join(' auto '),
            }
      }
      columnGap={4}
      rowGap={3}
      alignItems="stretch"
    >
      {columns.flatMap((column, index) =>
        index === 0
          ? [column]
          : [
              <Divider
                key={`divider-${index}`}
                orientation="vertical"
                flexItem
                sx={{
                  display: stacked ? 'none' : { xs: 'none', md: 'block' },
                  borderColor: pageBandDividerColor,
                }}
              />,
              column,
            ],
      )}
    </Box>
  );
}
